import {
    CssTokenEntry,
    DesignTokenTab,
    TypographyMixinEntry,
} from "./designTokensCatalog";

/**
 * One row of a design-token catalog table. Group rows carry only a label plus
 * their children; leaf rows carry either a css token or a typography mixin.
 */
export interface TokenViewRow {
    id: string;
    label: string;
    token?: CssTokenEntry;
    mixin?: TypographyMixinEntry;
    children?: TokenViewRow[];
}

const buildGroupRows = (tab: DesignTokenTab): TokenViewRow[] => {
    if ("groups" in tab) {
        return tab.groups.map((group) => ({
            id: group.label,
            label: group.label,
            children: group.tokens.map((token) => ({
                id: token.name,
                label: token.name,
                token,
            })),
        }));
    }

    return tab.typographyGroups.map((group) => ({
        id: group.label,
        label: group.label,
        children: group.mixins.map((mixin) => ({
            id: mixin.name,
            label: mixin.name,
            mixin,
        })),
    }));
};

const filterGroupRows = (groups: TokenViewRow[], search: string) =>
    groups.reduce<TokenViewRow[]>((matchingGroups, group) => {
        const children = (group.children ?? []).filter((child) =>
            child.label.toLowerCase().includes(search),
        );

        if (children.length > 0) {
            matchingGroups.push({ ...group, children });
        }

        return matchingGroups;
    }, []);

/**
 * Turns a catalog tab into expandable group rows with token/mixin children,
 * narrowed to the children whose name matches `search` (groups left without a
 * match drop out entirely).
 */
export const buildDesignTokenRows = ({
    tab,
    search,
}: {
    tab: DesignTokenTab;
    search?: string;
}): TokenViewRow[] => {
    const groups = buildGroupRows(tab);
    const term = search?.trim().toLowerCase() ?? "";

    return term.length === 0 ? groups : filterGroupRows(groups, term);
};

/** Expansion state that opens every group, keyed by the table's row ids. */
export const buildExpandedTokenGroups = (
    rows: TokenViewRow[],
): Record<string, boolean> =>
    Object.fromEntries(rows.map((row) => [row.id, true]));
